import { useMutation } from "@tanstack/react-query";
import { Loader2, RotateCcw, Send } from "lucide-react";
import React from "react";

import { syncBitrix, type InspectionDetail } from "@/api/okk_receiving";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { fmtDateTime } from "@/lib/format";
import { apiErrorText } from "@/lib/scan-ui/screen";

/**
 * Состояние синка проверки с задачей ОКК в Битриксе (`detail.bitrix`; приходит
 * во всех ответах-карточках, поэтому сбой после «Завершить»/правки виден сразу).
 * `error` — красная плашка с текстом и «Повторить»; `pending` — «Отправить».
 * Сбой самого повтора — не HTTP-ошибка, а снова `state=error`; HTTP-ошибка — текстом.
 */
export function BitrixBlock({
  detail,
  onSynced,
  onReread,
}: {
  detail: InspectionDetail;
  onSynced: (d: InspectionDetail) => void;
  onReread: () => void;
}): React.ReactElement {
  const b = detail.bitrix;
  const sync = useMutation({
    mutationFn: () => syncBitrix(detail.id),
    onSuccess: (d) => {
      onSynced(d);
      onReread();
    },
  });
  const busy = sync.isPending;
  const action = (label: string, icon: React.ReactNode) => (
    <Button
      size="sm"
      variant="outline"
      disabled={busy}
      onClick={() => sync.mutate()}
      data-testid="bitrix-action"
    >
      {busy ? <Loader2 className="animate-spin" /> : icon}
      {label}
    </Button>
  );
  const retryError = sync.isError ? (
    <div role="alert" className="text-sm text-rose-800" data-testid="bitrix-retry-error">
      Не удалось повторить: {apiErrorText(sync.error)}
    </div>
  ) : null;

  if (b.state === "error") {
    return (
      <div
        role="alert"
        data-testid="bitrix-block"
        data-state="error"
        className="space-y-2 rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800"
      >
        <div>
          <span className="font-medium">Битрикс: не отправлено.</span>{" "}
          <span data-testid="bitrix-error">{b.error}</span>
        </div>
        {action("Повторить", <RotateCcw />)}
        {retryError}
      </div>
    );
  }
  return (
    <Card
      data-testid="bitrix-block"
      data-state={b.state}
      className="flex flex-wrap items-center gap-2 p-3 text-sm"
    >
      {(b.state === "ok" || b.state === "pending") && (
        <span className="text-muted-foreground">Битрикс:</span>
      )}
      {b.state === "ok" && (
        <span className="text-xs text-muted-foreground">
          отправлено{b.synced_at ? ` ${fmtDateTime(b.synced_at)}` : ""}
        </span>
      )}
      {b.state === "pending" && (
        <>
          <span>ожидает отправки</span>
          {action("Отправить", <Send />)}
        </>
      )}
      {b.state === "disabled" && <span>Битрикс выключен</span>}
      {b.state === "no_task" && <span>у прихода нет задачи ОКК в Битриксе</span>}
      {retryError}
    </Card>
  );
}
